import moment, { isMoment } from 'moment'
import { isFunction, isNumber, isString } from 'lodash'

import { API_DATE, getTimezoneLessMoment } from '../momentjs'
import { EUROPEAN_DATE, InternshipStates, InternshipStatesAsString, InternshipStatesString } from '../constants'
import { formatAsArray, getEntityOrNull, getIdOrNull, inEnum, isNonEmptyArray, isObject, validateString } from '..'
import { ValidationFields } from '../validators'
import { isInstitutionUser, isSchoolUser, isSupervisor } from '../roles'

export const ExportKeys = Object.freeze({
  SECTOR_NAME: 'sector.name',
  SCHOOL_ABBREVIATION: 'school.abbreviation',
  SCHOOL_NAME: 'school.name',
  SECTION_NAME: 'internshipYear.sectionAbbreviation',
  SCHOOL_YEAR_NAME: 'internshipYear.schoolYearName',
  STUDENT_LASTNAME: 'student.lastname',
  STUDENT_FIRSTNAME: 'student.firstname',
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  COORDINATOR: 'coordinator.email',
  STATE: 'state',
  STUDENT_FREE_FIELDS: 'studentFreeFields',
  EMAIL: 'student.email',
  HOSPITAL_DEPARTMENT: 'hospitalDepartment.name'
})

// internship's states
export const PRE_ACCEPTED = -3
export const PENDING_STUDENT_DATA_MISSING = -2
export const PENDING_NO_SECTOR = -1
export const CREATED = 0
export const PENDING = 1
export const VALIDATED = 2 // actually unused
export const ACCEPTED = 3
export const AUTO_ACCEPTED = 4
export const REFUSED = 5
export const CANCELED = 6
export const ACCEPTED_UNMANAGED = 7
export const SCHEDULE_ADDED = 8
export const SCHEDULE_VALIDATED = 9
export const SCHEDULE_TO_VALIDATE = 10

export const StateTags = Object.freeze({
  [InternshipStates.PRE_ACCEPTED]: { label: InternshipStatesString[InternshipStates.PRE_ACCEPTED], color: 'cyan' },
  [InternshipStates.PENDING_STUDENT_DATA_MISSING]: { label: InternshipStatesString[InternshipStates.PENDING_STUDENT_DATA_MISSING], color: 'volcano' },
  [InternshipStates.PENDING_NO_SECTOR]: { label: InternshipStatesString[InternshipStates.PENDING_NO_SECTOR], color: '' },
  [InternshipStates.CREATED]: { label: InternshipStatesString[InternshipStates.CREATED], color: '' },
  [InternshipStates.PENDING]: { label: InternshipStatesString[InternshipStates.PENDING], color: 'blue' },
  [InternshipStates.ACCEPTED]: { label: InternshipStatesString[InternshipStates.ACCEPTED], color: 'green' },
  [InternshipStates.AUTO_ACCEPTED]: { label: InternshipStatesString[InternshipStates.AUTO_ACCEPTED], color: 'lime' },
  [InternshipStates.REFUSED]: { label: InternshipStatesString[InternshipStates.REFUSED], color: 'red' },
  [InternshipStates.CANCELED]: { label: InternshipStatesString[InternshipStates.CANCELED], color: 'orange' },
  [InternshipStates.ACCEPTED_UNMANAGED]: { label: InternshipStatesString[InternshipStates.ACCEPTED_UNMANAGED], color: 'lime' },
  [InternshipStates.SCHEDULE_ADDED]: { label: InternshipStatesString[InternshipStates.SCHEDULE_ADDED], color: 'green' },
  [InternshipStates.SCHEDULE_VALIDATED]: { label: InternshipStatesString[InternshipStates.SCHEDULE_VALIDATED], color: 'green' },
  [InternshipStates.SCHEDULE_TO_VALIDATE]: { label: InternshipStatesString[InternshipStates.SCHEDULE_TO_VALIDATE], color: 'green' }
})

export const getStateTag = state => StateTags[state] ?? null

const LOCKED_SCHOOL_STATES = new Set([
  InternshipStates.PRE_ACCEPTED,
  InternshipStates.CANCELED,
  InternshipStates.SCHEDULE_ADDED,
  InternshipStates.SCHEDULE_VALIDATED,
  InternshipStates.SCHEDULE_TO_VALIDATE
])

/** @enum {string} */
export const Ownership = Object.freeze({
  INSTITUTION: 'INSTITUTION',
  SCHOOL: 'SCHOOL'
})

/** @enum {string} */
export const ReadonlyProperties = Object.freeze({
  STATE: 'state',
  OWNERSHIP: 'ownership',
  BARE_SECTION: 'bareSection',
  NOTES: 'notes'
})

/** @enum {string} */
export const InternshipProperties = Object.freeze({
  SCHOOL: ValidationFields.SCHOOL,
  START_DATE: ValidationFields.START_DATE,
  END_DATE: ValidationFields.END_DATE,
  SECTION: ValidationFields.SECTION,
  SECTOR: ValidationFields.SECTOR,
  INSTITUTION: ValidationFields.INSTITUTION,
  STUDENT: ValidationFields.STUDENT,
  INTERNSHIP_YEAR: ValidationFields.INTERNSHIP_YEAR,
  STATE: ReadonlyProperties.STATE,
  OWNERSHIP: ReadonlyProperties.OWNERSHIP,
  BARE_SECTION: ReadonlyProperties.BARE_SECTION,
  DURATION: ValidationFields.DURATION,
  COMPENSATION: ValidationFields.COMPENSATION,
  EXTERNAL_EVALUATION_LINK: 'externalEvaluationLink'
})

export const InternshipValidators = Object.freeze({
  [InternshipProperties.SCHOOL]: i => validateString(getIdOrNull(i.school), { notEmpty: true }),
  [InternshipProperties.START_DATE]: i => isMoment(i.startDate),
  [InternshipProperties.END_DATE]: i => isMoment(i.endDate),
  [InternshipProperties.SECTION]: i => !!getEntityOrNull(i.section),
  [InternshipProperties.SECTOR]: i => !!getEntityOrNull(i.sector),
  [InternshipProperties.INSTITUTION]: i => validateString(getIdOrNull(i.institution), { notEmpty: true }),
  [InternshipProperties.STUDENT]: i => !!getEntityOrNull(i.student),
  [InternshipProperties.INTERNSHIP_YEAR]: i => !!getEntityOrNull(i.internshipYear)
})

export const makeInternship = ({ startDate, endDate, school, institution, sector, internshipYear, student }) => {
  return {
    startDate: startDate ?? moment(),
    endDate: endDate ?? moment(),
    school: school ?? null,
    institution: institution ?? null,
    sector: sector ?? null,
    student: student ?? null
  }
}

export const getSchoolName = i => {
  return i.school?.name ?? '/'
}

export const getSchoolAbbreviation = i => {
  return i.school?.abbreviation ?? i.school?.name ?? '/'
}

export const getSectionName = i => {
  return i.internshipYear?.sectionAbbreviation ?? '/'
}
export const getSectionFullName = i => {
  return i.internshipYear?.sectionName ?? i.section?.sectionToYear?.sectionName ?? '/'
}

export const getSectionNameOrFull = i => {
  return i.internshipYear?.sectionAbbreviation ?? i.internshipYear?.sectionName ?? i.section?.sectionToYear?.sectionAbbreviation ?? i.section?.abbreviation ?? '/'
}

export const getSchoolYearName = i => {
  return i.internshipYear?.schoolYearName ?? i.section?.schoolYearName ?? i.section?.sectionToYear?.schoolYearName ?? '/'
}

export const getInstitutionName = i => {
  return i.institution?.name ?? '/'
}

/**
 * The API only serializes the notes owned by the caller's own organisation, and at most one of them,
 * so there is nothing to filter nor to attribute client side.
 */
export const getInternshipNotes = i => formatAsArray(i?.notes)

export const getSectorName = i => {
  return i.sector?.name ?? '/'
}
export const getHospitalDepartmentName = i => {
  return i.sector?.hospitalDepartment?.name ?? '/'
}

export const getStudentLastname = i => {
  return i.student?.lastname ?? '/'
}

export const getStudentFirstname = i => {
  return i.student?.firstname ?? '/'
}

export const getStudentFullname = i => {
  return i.student?.firstname ? `${i.student?.firstname} ${i.student?.lastname}` : '/'
}

export const getRevertedStudentFullname = i => {
  return i.student?.lastname ? `${i.student?.lastname} ${i.student?.firstname}` : '/'
}

export const getStudentRegistrationNumber = i => {
  let matricule = i.student?.matricule

  if (!matricule) {
    matricule = '/'
  }

  return matricule
}

export const getStudentEmail = i => {
  return i.student?.email ?? '/'
}

export const getStudentPhoneNumber = i => {
  let phoneNumber = i.student?.phoneNumber

  if (!phoneNumber) {
    phoneNumber = '/'
  }

  return phoneNumber
}

export const getStudentBirthdate = i => {
  let birthdate = i.student?.birthdate

  if (!birthdate) {
    birthdate = '/'
  }

  if (birthdate !== '/') {
    birthdate = getTimezoneLessMoment(birthdate).format(EUROPEAN_DATE)
  }

  return birthdate
}
export const getState = i => {
  return i.state
}

export const getOwnership = i => {
  return i.ownership
}

export const getEndDate = i => {
  return getTimezoneLessMoment(i.endDate).format(EUROPEAN_DATE)
}

export const getStartDate = i => {
  return getTimezoneLessMoment(i.startDate).format(EUROPEAN_DATE)
}

export const getCoordinatorFullname = i => {
  return isObject(i.coordinator) ? `${i.coordinator?.firstname} ${i.coordinator?.lastname}` : '/'
}

export const setInternshipYear = (internship, internshipYear = null) => {
  if (isNumber(internshipYear)) {
    internship.internshipYear = { id: internshipYear }
  } else if (getEntityOrNull(internshipYear) !== null) {
    internship.internshipYear = internshipYear
  } else if (getEntityOrNull(internship.section) !== null && getEntityOrNull(internship.section.sectionToYear) !== null) {
    internship.internshipYear = internship.section.sectionToYear
  }

  return internship
}

export const getSectionKey = internship => {
  let key = -1

  if (internship.section?.sectionId) {
    key = internship.section?.sectionId
  } else if (internship.section?.sectionToYear) {
    key = internship.section.sectionToYear.section.id
  }

  return key
}

export const isContextDraggable = (user, i) => (
  (isInstitutionUser(user) && getState(i) < InternshipStates.PENDING) ||
  (isSchoolUser(user) && getState(i) < InternshipStates.ACCEPTED)
)

export const isUserOwnerSide = (user, internship) => (
  internship.ownership == null ||
  (isInstitutionUser(user) && internship.ownership === Ownership.INSTITUTION) ||
  (isSchoolUser(user) && internship.ownership === Ownership.SCHOOL)
)

export const isLockedState = i => getState(i) === InternshipStates.SCHEDULE_VALIDATED

export const getStudentEvaluationStartDate = i => getTimezoneLessMoment(i?.studentEvaluationStartDate)

export const isStudentEvaluationAvailable = (i, now = moment()) => {
  const startDate = getStudentEvaluationStartDate(i)

  return startDate === null || startDate.isSameOrBefore(now)
}

export const canUserEditProperty = (user, internship, propertyName) => {
  let isEditableKey = !isLockedState(internship) && !inEnum(ReadonlyProperties, propertyName)

  if (isEditableKey) {
    const isOwner = isUserOwnerSide(user, internship)

    if (isInstitutionUser(user)) {
      const notEditableProperties = new Set([InternshipProperties.INSTITUTION, InternshipProperties.STUDENT])
      const isDatesAndNotAccepted = (propertyName === InternshipProperties.START_DATE || propertyName === InternshipProperties.END_DATE) && getState(internship) < ACCEPTED

      isEditableKey = isOwner
        ? isDatesAndNotAccepted || (getIdOrNull(internship.student) === null && !notEditableProperties.has(propertyName)) || propertyName === InternshipProperties.SECTOR
        : propertyName === InternshipProperties.SECTOR
    } else if (isSchoolUser(user) && !LOCKED_SCHOOL_STATES.has(getState(internship))) {
      isEditableKey = isOwner
        ? propertyName !== InternshipProperties.SCHOOL
        : propertyName === InternshipProperties.STUDENT
    }
  }

  return isEditableKey
}

export const areEquivalent = (first, second) => {
  if (first == null || second == null) {
    return false
  }

  const withIdKeys = [InternshipProperties.SECTOR, InternshipProperties.SCHOOL, InternshipProperties.SECTION, InternshipProperties.STUDENT, InternshipProperties.BARE_SECTION]
  const datesKeys = [InternshipProperties.START_DATE, InternshipProperties.END_DATE]

  const unequivalentKeys = Object.keys(first).filter(key => {
    if (datesKeys.includes(key)) {
      return getTimezoneLessMoment(first[key]).format(API_DATE) !== getTimezoneLessMoment(second[key]).format(API_DATE)
    } else if (withIdKeys.includes(key)) {
      return getIdOrNull(first?.[key]) !== getIdOrNull(second?.[key])
    } else if (!isFunction(first[key])) {
      return first[key] !== second[key]
    }
  })

  return unequivalentKeys.length === 0
}

export const canDeleteInternship = (user, internships) => {
  let permissionGranted = false

  if (isSchoolUser(user) && !isSupervisor(user)) {
    permissionGranted = isNonEmptyArray(internships) && !internships.some(i => i.ownership === Ownership.INSTITUTION || i.state >= SCHEDULE_ADDED || [ACCEPTED, CANCELED].includes(i.state))
  } else if (isInstitutionUser(user)) {
    permissionGranted = isNonEmptyArray(internships) && !internships.some(i => i.ownership === Ownership.SCHOOL || i.state === SCHEDULE_VALIDATED)
  }

  return permissionGranted
}

export class Internship {
  constructor ({
    id = -1, institution = null, school = null, section = null, sector = null, student = null, startDate = moment(), endDate = moment(), state = null,
    ownership = null, bareSection = null, internshipYear = null, duration = null, compensation = false, notes = [], externalEvaluationLink = null
  } = {}) {
    this.id = id
    this.institution = getEntityOrNull(institution)
    this.school = getEntityOrNull(school)
    this.section = getEntityOrNull(section)
    this.sector = getEntityOrNull(sector)
    this.student = getEntityOrNull(student)
    this.startDate = isString(startDate) ? getTimezoneLessMoment(startDate) : startDate
    this.endDate = isString(endDate) ? getTimezoneLessMoment(endDate) : endDate
    this.state = inEnum(InternshipStatesAsString, state) || inEnum(InternshipStates, state) ? state : null
    this.ownership = inEnum(Ownership, ownership) ? ownership : null
    this.bareSection = getEntityOrNull(bareSection)
    this.internshipYear = setInternshipYear(this, internshipYear).internshipYear
    this.duration = duration
    this.compensation = compensation
    this.notes = formatAsArray(notes)
    this.externalEvaluationLink = externalEvaluationLink
  }

  static toDTO = object => new this(object).toRequestBody()

  toRequestBody = () => {
    const body = {}

    Object.entries(this).forEach(([property, value]) => {
      if (isMoment(value)) {
        body[property] = value.format(API_DATE)
      } else if (!isFunction(value) && !isObject(value) && !inEnum(ReadonlyProperties, property)) {
        body[property] = value
      } else if (!(isFunction(value) || inEnum(ReadonlyProperties, property))) {
        body[property] = getIdOrNull(value)
      }
    })

    return body
  }
}
